/**
 * 顧客詳細「商談」タブ — 受け取った書類 (2026-10)。
 *
 * PDF は置いた時点で「未分類」(deals/{id}/attachments) として保存し、種類は書類ごとに後から選ぶ。
 * 種類を選ぶと、その種類の保存先へ移して種類ごとの処理を行う (useMoveReceivedDocument):
 *   プロフォーマ   → manufacturer_quotes (uploadManufacturerQuote が保存済み PDF から番号と合計を AI で読み取る。割引額は手入力)
 *   最終インボイス → PurchaseOrder.officialOrderPdfPath (登録済みの発注では差し替えを確認し、前の書類は「未分類」へ)
 *   DHL 書類       → PurchaseOrder.importDocuments (輸入関税書類 / その他関連書類)
 * プロフォーマ / 最終インボイス / DHL 書類は TimeWaver 主軸カテゴリの商談だけ (商談詳細の発注カードと同じ保存先)。
 * 発注が必要な種類は、元の書類の発注 → 発注が 1 件ならそれ → 2 件以上なら行の中で選ぶ。
 */
import type { IsoOrTimestamp, PurchaseOrder } from '@goenhub/shared';
import { formatMoney } from '@goenhub/shared';
import { ExternalLink, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  RECEIVED_KIND_LABEL,
  useDealAttachments,
  useMoveReceivedDocument,
  useRemoveDealAttachment,
  useUploadDealAttachments,
  type ReceivedKind,
  type ReceivedSource,
} from '@/hooks/useDealAttachments';
import {
  useClearOfficialOrderPdf,
  useDealManufacturerQuotes,
  useDeleteManufacturerQuote,
  usePdfDownloadUrl,
  useRemoveImportDocument,
} from '@/hooks/useManufacturerQuotes';
import { useReturnToState } from '@/hooks/useReturnTo';
import { useDealPurchaseOrders } from '@/hooks/useSalesOps';
import { PdfDropZone } from '@/pages/deals/components/PdfDropZone';
import { paths } from '@/routes/paths';

type PurchaseOrderRow = PurchaseOrder & { id: string };

const KIND_BADGE: Record<ReceivedKind, NonNullable<BadgeProps['variant']>> = {
  other: 'default',
  pf: 'accent',
  fin: 'review',
  dhl_duty: 'draft',
  dhl_other: 'draft',
};
const KIND_ORDER: Record<ReceivedKind, number> = {
  other: 0,
  pf: 1,
  fin: 2,
  dhl_duty: 3,
  dhl_other: 3,
};
const NEEDS_ORDER: Record<ReceivedKind, boolean> = {
  other: false,
  pf: false,
  fin: true,
  dhl_duty: true,
  dhl_other: true,
};

interface Row extends ReceivedSource {
  key: string;
  /** 紐付く発注 (プロフォーマは請求経由、最終インボイス / DHL 書類は登録先) */
  linkedPo: PurchaseOrderRow | null;
  /** プロフォーマ: 読み取り中 / 結果 / 失敗 */
  reading?: boolean;
  readText?: string;
  readFailed?: boolean;
  /** 送金依頼に使われたプロフォーマ (種類変更・登録解除できない) */
  locked?: boolean;
  onRemove: () => Promise<unknown>;
}

interface Props {
  dealId: string;
  /** 主軸カテゴリーが TimeWaver か (プロフォーマ / 最終インボイス / DHL 書類を扱えるか) */
  isTimeWaver: boolean;
  /** メーカー取引通貨 (プロフォーマの合計表示に使う) */
  currency: string;
  /** uid → 表示名 */
  nameOf: (uid: string | null | undefined) => string;
}

export function DealReceivedDocuments({ dealId, isTimeWaver, currency, nameOf }: Props) {
  const mqs = useDealManufacturerQuotes(dealId);
  const pos = useDealPurchaseOrders(dealId);
  const atts = useDealAttachments(dealId);
  const upload = useUploadDealAttachments();
  const deletePf = useDeleteManufacturerQuote();
  const clearFin = useClearOfficialOrderPdf();
  const removeDhl = useRemoveImportDocument();
  const removeOther = useRemoveDealAttachment();
  const [uploadingCount, setUploadingCount] = useState(0);

  const allPos = useMemo(() => (pos.data ?? []) as PurchaseOrderRow[], [pos.data]);
  // 書類の登録先になる発注 (キャンセル除く)、古い順
  const orders = useMemo(
    () =>
      allPos
        .filter((p) => p.status !== 'cancelled')
        .sort((a, b) => toMillis(a.createdAt) - toMillis(b.createdAt)),
    [allPos],
  );

  // ── 一覧 (未分類を先頭に) ──
  const rows: Row[] = [];
  for (const a of atts.data ?? []) {
    rows.push({
      key: `other-${a.id}`,
      kind: 'other',
      attachmentId: a.id,
      storagePath: a.storagePath,
      fileName: a.fileName,
      uploadedAt: a.uploadedAt,
      uploadedBy: a.uploadedBy,
      linkedPo: null,
      onRemove: () => removeOther.mutateAsync({ dealId, attachmentId: a.id }),
    });
  }
  for (const mq of mqs.data ?? []) {
    const po = mq.invoiceId ? (allPos.find((p) => p.invoiceId === mq.invoiceId) ?? null) : null;
    const processed = mq.status === 'processed';
    const reading =
      !processed &&
      !mq.extractedAt &&
      !mq.extractionError &&
      mq.invoiceNumber == null &&
      mq.totalAmount == null;
    rows.push({
      key: `pf-${mq.id}`,
      kind: 'pf',
      manufacturerQuoteId: mq.id,
      storagePath: mq.pdfStoragePath,
      fileName: mq.originalFileName,
      uploadedAt: mq.uploadedAt,
      uploadedBy: mq.uploadedBy,
      linkedPo: po,
      reading,
      readFailed: !!mq.extractionError && !processed,
      readText: reading
        ? undefined
        : `番号 ${mq.invoiceNumber ?? '-'} ・ 合計 ${mq.totalAmount != null ? formatMoney(mq.totalAmount, currency) : '-'}`,
      locked: processed,
      onRemove: () => deletePf.mutateAsync({ dealId, manufacturerQuoteId: mq.id }),
    });
  }
  for (const po of allPos) {
    if (po.officialOrderPdfPath) {
      rows.push({
        key: `fin-${po.id}`,
        kind: 'fin',
        poId: po.id,
        storagePath: po.officialOrderPdfPath,
        fileName: po.officialOrderFileName ?? fileNameFromStoragePath(po.officialOrderPdfPath),
        uploadedAt: po.officialOrderUploadedAt ?? null,
        uploadedBy: po.officialOrderUploadedBy ?? '',
        linkedPo: po,
        onRemove: () => clearFin.mutateAsync({ dealId, poId: po.id }),
      });
    }
    for (const d of po.importDocuments ?? []) {
      rows.push({
        key: `dhl-${po.id}-${d.id}`,
        kind: d.type === 'import_duty' ? 'dhl_duty' : 'dhl_other',
        poId: po.id,
        importDocumentId: d.id,
        storagePath: d.storagePath,
        fileName: d.fileName,
        uploadedAt: d.uploadedAt,
        uploadedBy: d.uploadedBy,
        linkedPo: po,
        onRemove: () => removeDhl.mutateAsync({ dealId, poId: po.id, documentId: d.id }),
      });
    }
  }
  rows.sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind]);

  const handleFiles = async (files: File[]) => {
    setUploadingCount(files.length);
    try {
      const r = await upload.mutateAsync({ dealId, files });
      toast.success(
        isTimeWaver
          ? `${r.count} 件を「未分類」で保存しました。書類ごとに「種類」を選んでください`
          : `${r.count} 件を保存しました`,
      );
    } catch (err) {
      toast.error(`保存に失敗しました: ${(err as Error).message}`);
    } finally {
      setUploadingCount(0);
    }
  };

  const hint = isTimeWaver
    ? `置いた書類は「未分類」で保存されます。書類ごとに「種類」を選ぶと、プロフォーマは番号と合計を読み取り、最終インボイス・DHL 書類は発注に登録されます。${
        orders.length === 0
          ? '最終インボイス・DHL 書類は、入金確認後に発注が作られてから選べます。'
          : ''
      }`
    : '置いた書類は「未分類」で保存されます。';

  const loading = mqs.isLoading || pos.isLoading || atts.isLoading;

  return (
    <div className="space-y-2">
      <div className="pb-1.5 border-b border-[var(--border)]">
        <span className="text-[11px] font-semibold text-[var(--text-secondary)]">
          受け取った書類（{rows.length}）
        </span>
      </div>

      <PdfDropZone
        multiple
        disabled={upload.isPending}
        label="PDF をここにドロップ（複数可） ／ クリックして選択"
        hint={hint}
        onFiles={(files) => void handleFiles(files)}
      />

      {uploadingCount > 0 && (
        <div className="flex items-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-2 text-[11px] text-[var(--accent)]">
          <Loader2 className="size-3 animate-spin shrink-0" />
          {uploadingCount} 件を保存しています…
        </div>
      )}

      {loading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <p className="text-xs text-[var(--text-muted)] py-1">受け取った書類はまだありません</p>
      ) : (
        <div className="divide-y divide-[var(--border)]">
          {rows.map((r) => (
            <ReceivedRow
              key={r.key}
              dealId={dealId}
              row={r}
              isTimeWaver={isTimeWaver}
              orders={orders}
              currency={currency}
              nameOf={nameOf}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ReceivedRow({
  dealId,
  row,
  isTimeWaver,
  orders,
  currency,
  nameOf,
}: {
  dealId: string;
  row: Row;
  isTimeWaver: boolean;
  orders: PurchaseOrderRow[];
  currency: string;
  nameOf: (uid: string | null | undefined) => string;
}) {
  const pdfUrl = usePdfDownloadUrl(row.storagePath);
  // 「発注カードで入力」で商談詳細を開く時の戻り先 (顧客詳細)
  const returnToState = useReturnToState('顧客詳細');
  const move = useMoveReceivedDocument();
  // 発注が 2 件以上ある時に「どの発注に登録するか」を聞いている種類
  const [askOrderFor, setAskOrderFor] = useState<ReceivedKind | null>(null);
  const [busyText, setBusyText] = useState('');

  const canChoose = isTimeWaver && !row.locked;
  const noOrders = orders.length === 0;
  const who = nameOf(row.uploadedBy);
  const orderLabel =
    row.kind === 'other' ? '' : row.linkedPo ? row.linkedPo.poNumber : 'どの発注にも紐付いていない';
  const meta = [orderLabel, fmtDay(row.uploadedAt), who].filter(Boolean).join(' ・ ');
  const cardUrl = `${paths.dealDetail.replace(':id', dealId)}?tab=fulfillment${
    row.linkedPo ? `&po=${row.linkedPo.id}` : ''
  }`;

  const apply = async (target: ReceivedKind, po: PurchaseOrderRow | null) => {
    setAskOrderFor(null);
    let replacedFinal: Parameters<typeof move.mutateAsync>[0]['replacedFinal'] = null;
    if (
      target === 'fin' &&
      po?.officialOrderPdfPath &&
      po.officialOrderPdfPath !== row.storagePath
    ) {
      const currentName =
        po.officialOrderFileName ?? fileNameFromStoragePath(po.officialOrderPdfPath);
      if (
        !confirm(
          `発注 ${po.poNumber} には最終インボイス「${currentName}」が登録済みです。差し替えますか？（いまの最終インボイスは「未分類」に移します）`,
        )
      ) {
        return;
      }
      replacedFinal = {
        storagePath: po.officialOrderPdfPath,
        fileName: currentName,
        uploadedAt: po.officialOrderUploadedAt ?? null,
        uploadedBy: po.officialOrderUploadedBy ?? '',
      };
    }
    setBusyText(
      target === 'pf'
        ? '番号と合計を読み取り中…（このまま他の操作ができます）'
        : `「${RECEIVED_KIND_LABEL[target]}」に登録しています…`,
    );
    try {
      const r = await move.mutateAsync({
        dealId,
        source: row,
        target,
        po: po ? { id: po.id, invoiceId: po.invoiceId ?? null } : null,
        replacedFinal,
      });
      if (target === 'pf' && r.extracted) {
        if (r.extracted.extractionError) {
          toast.warning(
            `プロフォーマにしました（番号と合計は読み取れませんでした: ${r.extracted.extractionError}）。発注カードで手動入力してください`,
          );
        } else {
          toast.success(
            `プロフォーマにしました。読み取り: ${r.extracted.invoiceNumber ?? '-'} / ${formatMoney(r.extracted.totalAmount ?? 0, currency)}`,
          );
        }
      } else {
        toast.success(`種類を「${RECEIVED_KIND_LABEL[target]}」にしました`);
      }
    } catch (err) {
      toast.error(`種類を変更できませんでした: ${(err as Error).message}`);
    } finally {
      setBusyText('');
    }
  };

  const choose = (target: ReceivedKind) => {
    if (target === row.kind) return;
    const currentPo =
      row.linkedPo && orders.some((p) => p.id === row.linkedPo?.id) ? row.linkedPo : null;
    // 発注が必要な種類、または発注のあるプロフォーマ: 元の発注 → 1 件ならそれ → 2 件以上なら選んでもらう
    if (NEEDS_ORDER[target] || (target === 'pf' && orders.length > 0)) {
      if (currentPo) return void apply(target, currentPo);
      if (orders.length === 1) return void apply(target, orders[0]);
      if (orders.length >= 2) return setAskOrderFor(target);
    }
    void apply(target, null);
  };

  const handleRemove = async () => {
    if (!confirm(`「${row.fileName}」の登録を外しますか？（PDF ファイル自体は保管場所に残ります）`))
      return;
    setBusyText('登録を外しています…');
    try {
      await row.onRemove();
      toast.success('登録を外しました');
    } catch (err) {
      toast.error(`失敗しました: ${(err as Error).message}`);
    } finally {
      setBusyText('');
    }
  };

  return (
    <div className="py-2 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {canChoose ? (
          <select
            value={row.kind}
            onChange={(e) => choose(e.target.value as ReceivedKind)}
            disabled={!!busyText}
            aria-label="書類の種類"
            className={`h-7 px-1.5 text-[11px] rounded-sm border bg-[var(--bg-input)] ${
              row.kind === 'other'
                ? 'border-[var(--accent-amber)] text-[var(--accent-amber)] font-semibold'
                : 'border-[var(--border)]'
            }`}
          >
            <option value="other">{RECEIVED_KIND_LABEL.other}</option>
            <option value="pf">{RECEIVED_KIND_LABEL.pf}</option>
            <option value="fin" disabled={noOrders}>
              {RECEIVED_KIND_LABEL.fin}
              {noOrders ? '（発注作成後）' : ''}
            </option>
            <option value="dhl_duty" disabled={noOrders}>
              {RECEIVED_KIND_LABEL.dhl_duty}
              {noOrders ? '（発注作成後）' : ''}
            </option>
            <option value="dhl_other" disabled={noOrders}>
              {RECEIVED_KIND_LABEL.dhl_other}
              {noOrders ? '（発注作成後）' : ''}
            </option>
          </select>
        ) : (
          <Badge variant={KIND_BADGE[row.kind]}>{RECEIVED_KIND_LABEL[row.kind]}</Badge>
        )}
        <div className="flex-1 min-w-[160px] space-y-0.5">
          <div className="font-mono text-[11px] truncate text-[var(--text-primary)]">
            {row.fileName}
          </div>
          {meta && <div className="text-[10px] text-[var(--text-muted)]">{meta}</div>}
          {canChoose && row.kind === 'other' && !busyText && !askOrderFor && (
            <div className="text-[11px] text-[var(--accent-amber)]">← 種類を選んでください</div>
          )}
          {row.kind === 'pf' && row.reading && !busyText && (
            <div className="inline-flex items-center gap-1 text-[11px] text-[var(--accent)]">
              <Loader2 className="size-3 animate-spin" />
              番号と合計を読み取り中…
            </div>
          )}
          {row.kind === 'pf' && !row.reading && row.readText && (
            <div className="text-[11px] text-[var(--text-secondary)]">
              {row.readFailed ? '読み取れませんでした（手動で入力）' : row.readText}
              {!row.locked && (
                <>
                  {' ・ 割引額は手入力 '}
                  <Link
                    to={cardUrl}
                    state={returnToState}
                    className="text-[var(--accent)] hover:underline"
                  >
                    発注カードで入力
                  </Link>
                </>
              )}
            </div>
          )}
        </div>
        {row.locked && <Badge variant="approved">発注済み</Badge>}
        <Button
          variant="secondary"
          size="sm"
          onClick={() => pdfUrl.data && window.open(pdfUrl.data, '_blank')}
          disabled={!pdfUrl.data}
        >
          <ExternalLink className="size-3" />
          開く
        </Button>
        {!row.locked && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void handleRemove()}
            disabled={!!busyText}
          >
            削除
          </Button>
        )}
      </div>

      {askOrderFor && (
        <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-[var(--accent)] bg-[var(--accent-subtle)] px-2.5 py-1.5 text-[11px]">
          <span className="text-[var(--text-primary)]">
            「{RECEIVED_KIND_LABEL[askOrderFor]}」をどの発注に登録しますか？
          </span>
          {orders.map((po) => (
            <button
              key={po.id}
              type="button"
              onClick={() => void apply(askOrderFor, po)}
              className="h-7 px-2.5 rounded-md border border-[var(--border)] bg-[var(--bg-surface)] font-mono hover:border-[var(--accent)]"
            >
              {po.poNumber}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setAskOrderFor(null)}
            className="h-7 px-2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
          >
            やめる
          </button>
        </div>
      )}

      {busyText && (
        <div className="inline-flex items-center gap-1.5 text-[11px] text-[var(--accent)]">
          <Loader2 className="size-3 animate-spin" />
          {busyText}
        </div>
      )}
    </div>
  );
}

function toMillis(v: IsoOrTimestamp | null | undefined): number {
  if (!v) return 0;
  if (typeof v === 'string') return new Date(v).getTime() || 0;
  if (typeof v === 'object' && 'toMillis' in v) return (v as { toMillis: () => number }).toMillis();
  return 0;
}

function fmtDay(v: IsoOrTimestamp | null | undefined): string {
  if (!v) return '';
  const d =
    typeof v === 'string'
      ? new Date(v)
      : typeof v === 'object' && 'toDate' in v
        ? (v as { toDate: () => Date }).toDate()
        : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ja-JP');
}

/** 旧データ (元ファイル名を持たない最終インボイス) 用: `{timestamp}-{safeName}` から表示名を取り出す。 */
function fileNameFromStoragePath(path: string): string {
  const base = path.split('/').pop() ?? '';
  return base.replace(/^\d+-/, '') || base;
}
